import React, { useEffect, useRef, useState } from 'react';

/**
 * ClickParticles
 * - On click anywhere (except interactive UI): spawns an orange particle burst.
 * - On mouse move: leaves a subtle glowing cursor trail behind.
 *
 * Perf-optimised:
 *  - Single rAF loop for both bursts and trail (no setInterval).
 *  - Trail samples mouse movement at most every 40 ms.
 *  - Loop self-stops when no particles are alive.
 */
export default function ClickParticles({ color = '#FF7A00', count = 8 }) {
  const [, setTick] = useState(0); // forces re-render when particle arrays mutate
  const burstsRef = useRef([]);
  const trailRef = useRef([]);
  const rafRef = useRef(null);
  const lastTrailRef = useRef(0);

  // --- Animation loop ---
  useEffect(() => {
    const loop = () => {
      const bursts = burstsRef.current;
      const trail = trailRef.current;

      // Bursts: gravity + fade
      for (let i = bursts.length - 1; i >= 0; i--) {
        const p = bursts[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.1;
        p.opacity -= 0.03;
        if (p.opacity <= 0) bursts.splice(i, 1);
      }
      // Trail: float + fade
      for (let i = trail.length - 1; i >= 0; i--) {
        const p = trail[i];
        p.x += p.vx;
        p.y += p.vy;
        p.alpha -= 0.04;
        if (p.alpha <= 0) trail.splice(i, 1);
      }

      setTick((t) => (t + 1) & 0xffff);

      if (bursts.length === 0 && trail.length === 0) {
        rafRef.current = null;
        return;
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    const start = () => {
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(loop);
      }
    };

    // --- Click burst ---
    const onClick = (e) => {
      if (
        e.target.closest('button') ||
        e.target.closest('a') ||
        e.target.closest('input') ||
        e.target.closest('textarea') ||
        e.target.closest('select') ||
        e.target.closest('label')
      ) {
        return;
      }
      for (let i = 0; i < count; i++) {
        burstsRef.current.push({
          id: Date.now() + i + Math.random(),
          x: e.clientX,
          y: e.clientY,
          vx: (Math.random() - 0.5) * 6,
          vy: (Math.random() - 0.7) * 6 - 2,
          size: 3 + Math.random() * 5,
          opacity: 1,
        });
      }
      start();
    };

    // --- Trail (throttled ~25 samples/sec) ---
    const onMove = (e) => {
      const now = performance.now();
      if (now - lastTrailRef.current < 40) return;
      lastTrailRef.current = now;

      trailRef.current.push({
        id: Math.random(),
        x: e.clientX,
        y: e.clientY,
        size: Math.random() * 6 + 2,
        alpha: 1,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2 - 1,
      });
      // cap trail length
      if (trailRef.current.length > 30) trailRef.current.shift();
      start();
    };

    window.addEventListener('click', onClick);
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener('mousemove', onMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [count]);

  return (
    <div className="fixed inset-0 pointer-events-none z-[9999]" data-testid="click-particles-layer">
      {trailRef.current.map((p) => (
        <div
          key={`t-${p.id}`}
          className="absolute rounded-full"
          style={{
            left: p.x,
            top: p.y,
            width: p.size,
            height: p.size,
            backgroundColor: color,
            opacity: p.alpha,
            boxShadow: `0 0 15px ${color}`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      ))}
      {burstsRef.current.map((p) => (
        <div
          key={`c-${p.id}`}
          className="absolute rounded-full"
          style={{
            left: p.x,
            top: p.y,
            width: p.size,
            height: p.size,
            backgroundColor: color,
            opacity: p.opacity,
            boxShadow: `0 0 10px ${color}`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      ))}
    </div>
  );
}
